import { useCallback, useEffect, useState } from 'react'
import { createFileRoute, useSearch, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { CurrencyDisplay } from '@/components/ui/currency-display'
import { PhoneNumber } from '@/components/ui/phone-number'
import { toast } from 'sonner'
import { PhoneOutgoing, Phone, Receipt, RefreshCw, ArrowDownToLine } from 'lucide-react'
import { BalanceHeroCard } from '@/components/account/BalanceHeroCard'
import { AutoTopupConfigCard } from '@/components/account/AutoTopupConfigCard'
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyMedia, EmptyContent } from '@/components/ui/empty'
import { accountQueries } from '@/services/accountApi'
import { autoTopupQueries } from '@/services/autoTopupApi'
import { useCallHistoryQuery, CALL_HISTORY_PAGE_SIZE } from '@/hooks/useCallHistoryQuery'
import { useTopUpHistory, TOPUP_HISTORY_PAGE_SIZE } from '@/hooks/useTopUpHistory'
import { useLang } from '@/hooks/useLang'
import { useRootConfig } from '@/contexts/RootConfigContext'
import StripeTopUpModal from '@/components/StripeTopUpModal'
import StripeSetupIntentModal from '@/components/StripeSetupIntentModal'
import type { CallHistoryItem, TopUpItem } from '@/types/account'
import {
  TRANSACTION_STATUS_LABELS,
  TRANSACTION_TYPE_LABELS,
  CALL_STATUS_LABELS,
  getStatusBadgeVariant,
} from '@/types/account'
import { AccountLayout } from './-layout'

export const Route = createFileRoute('/$lang/account/billing')({
  component: BillingRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    topup: search.topup === 'true',
  }),
})

function BillingRoute() {
  const { t } = useTranslation(['app', 'common'])
  const queryClient = useQueryClient()
  const lang = useLang()
  const { stripePublishableKey } = useRootConfig()
  const search = useSearch({ from: '/$lang/account/billing' })
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false)
  const [isSetupIntentModalOpen, setIsSetupIntentModalOpen] = useState(false)

  const {
    data: callHistoryData,
    isLoading: isLoadingCallHistory,
    isFetching: isFetchingCallHistory,
    error: callHistoryError,
    page,
    setPage,
    refresh: refreshCallHistory,
  } = useCallHistoryQuery()

  const {
    data: topUpData,
    isLoading: isLoadingTopUps,
    isFetching: isFetchingTopUps,
    error: topUpError,
    page: topUpPage,
    setPage: setTopUpPage,
    refresh: refreshTopUps,
  } = useTopUpHistory()

  useEffect(() => {
    if (search.topup) {
      setIsTopUpModalOpen(true)
    }
  }, [search.topup])

  // Listen for custom event to open top-up modal
  useEffect(() => {
    const handleOpenTopupModal = () => {
      setIsTopUpModalOpen(true)
    }

    window.addEventListener('open-topup-modal', handleOpenTopupModal)
    return () => {
      window.removeEventListener('open-topup-modal', handleOpenTopupModal)
    }
  }, [])

  useEffect(() => {
    if (callHistoryError) toast.error(callHistoryError.message)
  }, [callHistoryError])

  useEffect(() => {
    if (topUpError) toast.error(topUpError.message)
  }, [topUpError])

  const formatDuration = useCallback(
    (totalSeconds: number) => {
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      if (minutes > 0 && seconds > 0) {
        return t('account.callHistoryLabels.duration', { minutes, seconds })
      } else if (minutes > 0) {
        return t('account.callHistoryLabels.duration_minutesOnly', { minutes })
      } else {
        return t('account.callHistoryLabels.duration_secondsOnly', { seconds })
      }
    },
    [t]
  )

  const handleTopUpSubmit = async () => {
    // Invalidate transaction history and auto-topup config (may have been enabled during payment)
    queryClient.invalidateQueries({ queryKey: accountQueries.topUpHistoryBase })
    queryClient.invalidateQueries({ queryKey: autoTopupQueries.all })
  }

  // ── Call history columns ──────────────────────────────────────────────────
  const callColumns: ColumnDef<CallHistoryItem>[] = [
    {
      id: 'icon',
      meta: { cardRole: 'icon', label: '' },
      enableHiding: false,
      header: () => null,
      cell: () => (
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
          <PhoneOutgoing className="h-4 w-4" />
        </div>
      ),
    },
    {
      accessorKey: 'destinationNumber',
      meta: { cardRole: 'primary' },
      header: ({ column }) => <SortableHeader column={column} label={t('account.callHistoryTable.to')} />,
      cell: ({ row }) => (
        <PhoneNumber number={row.original.destinationNumber} className="text-sm font-medium" />
      ),
    },
    {
      accessorKey: 'startTime',
      meta: { cardRole: 'secondary' },
      header: ({ column }) => <SortableHeader column={column} label={t('account.callHistoryTable.dateTime')} />,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {new Date(row.original.startTime).toLocaleString()} · {formatDuration(row.original.duration)}
        </span>
      ),
    },
    {
      accessorKey: 'cost',
      meta: { cardRole: 'value' },
      header: ({ column }) => <SortableHeader column={column} label={t('account.callHistoryTable.cost')} />,
      sortingFn: (a, b) => a.original.cost.amount - b.original.cost.amount,
      cell: ({ row }) => (
        <CurrencyDisplay
          amount={row.original.cost.amount}
          currency={row.original.cost.currency}
          debit hideSign hideColor inCents
        />
      ),
    },
    {
      accessorKey: 'status',
      meta: { cardRole: 'badge' },
      header: ({ column }) => <SortableHeader column={column} label={t('account.callHistoryTable.status')} />,
      cell: ({ row }) => {
        const status = row.original.status
        const label = CALL_STATUS_LABELS[status] || status
        return (
          <Badge variant={getStatusBadgeVariant(status)} className="text-xs">
            {label}
          </Badge>
        )
      },
    },
  ]

  // ── Transaction columns ───────────────────────────────────────────────────
  const transactionColumns: ColumnDef<TopUpItem>[] = [
    {
      id: 'icon',
      meta: { cardRole: 'icon' },
      enableHiding: false,
      header: () => null,
      cell: () => (
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
          <ArrowDownToLine className="h-4 w-4" />
        </div>
      ),
    },
    {
      accessorKey: 'type',
      meta: { cardRole: 'primary' },
      header: ({ column }) => <SortableHeader column={column} label={t('account.transactionHistoryTable.type')} />,
      cell: ({ row }) => {
        const typeLabel = row.original.type
          ? (TRANSACTION_TYPE_LABELS[row.original.type] || row.original.type)
          : '—'
        return <span className="text-sm font-medium">{typeLabel}</span>
      },
    },
    {
      accessorKey: 'createdAt',
      meta: { cardRole: 'secondary' },
      header: ({ column }) => <SortableHeader column={column} label={t('account.transactionHistoryTable.date')} />,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => <SortableHeader column={column} label={t('account.transactionHistoryTable.amount')} />,
      meta: { cardRole: 'value' },
      cell: ({ row }) => {
        const amount = (row.original.payment?.amount ?? row.original.amount) / 100
        const currency = row.original.payment?.currency ?? row.original.currency
        return <CurrencyDisplay amount={amount} currency={currency} />
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <SortableHeader column={column} label={t('account.transactionHistoryTable.status')} />,
      meta: { cardRole: 'badge' },
      cell: ({ row }) => {
        const status = row.original.status
        const label = TRANSACTION_STATUS_LABELS[status] || status
        return (
          <Badge variant={getStatusBadgeVariant(status)} className="text-xs">
            {label}
          </Badge>
        )
      },
    },
  ]

  return (
    <AccountLayout>
      <div className="space-y-8">
        {/* Hero Balance Card */}
        <BalanceHeroCard
          onTopUp={() => setIsTopUpModalOpen(true)}
          secondaryCta={
            <Button
              variant="ghost"
              shape="pill"
              className="border border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
              asChild
            >
              <Link to="/$lang/pricing" params={{ lang }}>
                {t('account.viewPricing')}
              </Link>
            </Button>
          }
        />

        {/* Auto Top-up Config Card */}
        <AutoTopupConfigCard
          onEnable={() => setIsSetupIntentModalOpen(true)}
        />

        {/* History */}
        <Tabs defaultValue="calls">
          <Card flush className="overflow-hidden shadow-sm">
            <CardContent>
              {/* Persistent tab switcher — always visible regardless of empty/error state */}
              <div className="flex items-center justify-between py-4">
                <TabsList>
                  <TabsTrigger value="calls" className="gap-2">
                    <Phone className="h-4 w-4" />
                    {t('account.callHistory')}
                  </TabsTrigger>
                  <TabsTrigger value="transactions" className="gap-2">
                    <Receipt className="h-4 w-4" />
                    {t('account.transactionHistory')}
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Call History */}
              <TabsContent value="calls" className="mt-0">
                {callHistoryError ? (
                  <Empty>
                    <EmptyMedia variant="icon" className="bg-destructive/10 text-destructive">
                      <RefreshCw className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyHeader>
                      <EmptyDescription>{t('account.callHistoryError')}</EmptyDescription>
                    </EmptyHeader>
                    <EmptyContent>
                      <Button variant="outline" size="sm" onClick={refreshCallHistory}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        {t('common.retry')}
                      </Button>
                    </EmptyContent>
                  </Empty>
                ) : callHistoryData && callHistoryData.items.length === 0 && !isLoadingCallHistory ? (
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Phone className="h-6 w-6" />
                      </EmptyMedia>
                      <EmptyTitle>{t('account.noCallHistory')}</EmptyTitle>
                      <EmptyDescription>{t('account.callHistoryEmpty')}</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                ) : (
                  <DataTable
                    columns={callColumns}
                    data={callHistoryData?.items ?? []}
                    enableSearch={false}
                    toolbarSlot={(viewOptions) => viewOptions ? <div className="pb-4">{viewOptions}</div> : null}
                    manualPagination={{
                      pageIndex: page,
                      pageSize: CALL_HISTORY_PAGE_SIZE,
                      totalItems: callHistoryData?.totalItems ?? 0,
                      onPageChange: setPage,
                      isLoading: isLoadingCallHistory || isFetchingCallHistory,
                    }}
                  />
                )}
              </TabsContent>

              {/* Transaction History */}
              <TabsContent value="transactions" className="mt-0">
                {topUpError ? (
                  <Empty>
                    <EmptyMedia variant="icon" className="bg-destructive/10 text-destructive">
                      <RefreshCw className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyHeader>
                      <EmptyDescription>{t('account.transactionError')}</EmptyDescription>
                    </EmptyHeader>
                    <EmptyContent>
                      <Button variant="outline" size="sm" onClick={refreshTopUps}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        {t('common.retry')}
                      </Button>
                    </EmptyContent>
                  </Empty>
                ) : topUpData && topUpData.items.length === 0 && !isLoadingTopUps ? (
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Receipt className="h-6 w-6" />
                      </EmptyMedia>
                      <EmptyTitle>{t('account.noTopUps')}</EmptyTitle>
                      <EmptyDescription>{t('account.transactionHistoryEmpty')}</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                ) : (
                  <DataTable
                    columns={transactionColumns}
                    data={topUpData?.items ?? []}
                    enableSearch={false}
                    toolbarSlot={(viewOptions) => viewOptions ? <div className="pb-4">{viewOptions}</div> : null}
                    manualPagination={{
                      pageIndex: topUpPage,
                      pageSize: TOPUP_HISTORY_PAGE_SIZE,
                      totalItems: topUpData?.totalItems ?? 0,
                      onPageChange: setTopUpPage,
                      isLoading: isLoadingTopUps || isFetchingTopUps,
                    }}
                  />
                )}
              </TabsContent>
            </CardContent>
          </Card>
        </Tabs>

        <StripeTopUpModal
          isOpen={isTopUpModalOpen}
          onClose={() => setIsTopUpModalOpen(false)}
          onTopUp={handleTopUpSubmit}
          stripePublishableKey={stripePublishableKey}
        />

        <StripeSetupIntentModal
          isOpen={isSetupIntentModalOpen}
          onClose={() => setIsSetupIntentModalOpen(false)}
          stripePublishableKey={stripePublishableKey}
        />
      </div>
    </AccountLayout>
  )
}
